import { useSelect } from '@refinedev/antd';
import { useTranslate } from '@refinedev/core';
import { Flex, Select, Typography, theme } from 'antd';
import type { SelectProps } from 'antd';
import type { PublicationListOut } from '@/api/generated/schemas';
import { PublicationCoverThumb } from '@/components/user_catalog/publications/PublicationCoverThumb';
import { PublicationStatusTags } from '@/components/user_catalog/publications/PublicationStatusTags';

const OPTION_COVER_SIZE = 32;

interface PublicationPickerProps extends Omit<
  SelectProps,
  'options' | 'children'
> {
  preselectedIds?: number[];
}

const PublicationOption = ({
  publication,
}: {
  publication: PublicationListOut;
}) => {
  const { token } = theme.useToken();

  return (
    <Flex gap={token.marginXS} align="center">
      <PublicationCoverThumb
        url={publication.cover_url}
        size={OPTION_COVER_SIZE}
      />
      <Typography.Text ellipsis style={{ flex: 1, minWidth: 0 }}>
        {publication.title}
      </Typography.Text>
      <PublicationStatusTags publication={publication} />
    </Flex>
  );
};

/**
 * Searchable publication select. Server-side `q` contains search; options
 * show cover, title and the per-level moderation status, while the closed
 * select shows the plain title.
 */
export const PublicationPicker = ({
  preselectedIds,
  ...selectRest
}: PublicationPickerProps) => {
  const t = useTranslate();

  const { selectProps, query, defaultValueQuery } =
    useSelect<PublicationListOut>({
      resource: 'user_catalog.publications',
      optionLabel: 'title',
      optionValue: 'id',
      onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
      defaultValue: preselectedIds?.length ? preselectedIds : undefined,
    });

  // Preselected records first so an already-picked id always has a label,
  // even when it is absent from the current search page.
  const byId = new Map<number, PublicationListOut>(
    [...(defaultValueQuery.data?.data ?? []), ...(query.data?.data ?? [])].map(
      (publication) => [publication.id, publication]
    )
  );

  const options = [...byId.values()].map((publication) => ({
    value: publication.id,
    title: publication.title,
    label: <PublicationOption publication={publication} />,
  }));

  return (
    <Select
      {...selectProps}
      options={options}
      // The rich label is dropdown-only; the closed select stays one line.
      optionLabelProp="title"
      showSearch
      filterOption={false}
      placeholder={t('user_catalog.common.publications_editor.placeholder')}
      {...selectRest}
    />
  );
};
